/**
 * Admin / Manager surface to create and manage announcements.
 * Rendered as a dialog opened from a "Manage announcements" button on the
 * Dashboard for Admin/Manager.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Megaphone, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { fmtDateTime } from "@/lib/dates";

const SEVERITIES = [
  { value: "info", label: "Info (blue)" },
  { value: "warning", label: "Warning (amber)" },
  { value: "critical", label: "Critical (red + sound)" },
];

const AUDIENCES = [
  { value: "all", label: "Everyone" },
  { value: "managers", label: "Managers only" },
  { value: "executives", label: "Executives only" },
  { value: "admins", label: "Admins only" },
];

const EMPTY = () => ({ title: "", body: "", severity: "info", audience: "all", expires_at: "" });

export default function AnnouncementsManager({ trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/announcements/all");
      setItems(r.data || []);
    } catch (e) { console.error("announcements/save failed:", e); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/announcements", {
        ...form,
        expires_at: form.expires_at || null,
      });
      toast.success("Announcement posted");
      setForm(EMPTY());
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete announcement "${a.title}"?`)) return;
    try {
      await api.delete(`/announcements/${a.id}`);
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { active: !a.active });
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="rounded-sm" data-testid="btn-open-announcements">
            <Megaphone size={14} className="mr-1.5" /> Announcements
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-3xl" data-testid="dialog-announcements">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Announcements</DialogTitle>
        </DialogHeader>

        {/* Create new */}
        <div className="border border-slate-200 rounded-sm p-3 space-y-3" data-testid="ann-create">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">New announcement</div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 rounded-sm"
              placeholder="e.g. Server maintenance tonight 10–11pm"
              maxLength={160}
              data-testid="input-ann-title"
            />
          </div>
          <div>
            <Label className="text-xs">Details (optional)</Label>
            <Textarea
              rows={3}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="mt-1 rounded-sm"
              maxLength={2000}
              data-testid="input-ann-body"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="select-ann-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="select-ann-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Expires at (optional)</Label>
              <Input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                className="mt-1 rounded-sm font-mono-data"
                data-testid="input-ann-expires"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={saving || !form.title.trim()}
              className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
              data-testid="btn-post-announcement"
            >
              <Plus size={14} className="mr-1.5" /> {saving ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="border border-slate-200 rounded-sm overflow-x-auto max-h-72 overflow-y-auto" data-testid="ann-list">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Severity</th>
                <th className="text-left px-3 py-2">Audience</th>
                <th className="text-left px-3 py-2">Posted</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 italic">No announcements yet</td></tr>
              )}
              {items.map((a) => {
                const canMutate = user.role === "Admin" || a.created_by === user.user_id;
                return (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`ann-row-${a.id}`}>
                    <td className="px-3 py-1.5 max-w-[260px] truncate font-medium">{a.title}</td>
                    <td className="px-3 py-1.5"><span className="uppercase text-[10px] font-semibold">{a.severity}</span></td>
                    <td className="px-3 py-1.5">{a.audience}</td>
                    <td className="px-3 py-1.5 font-mono-data text-[10px] text-slate-500">{fmtDateTime(a.created_at)}</td>
                    <td className="px-3 py-1.5">
                      <span className={a.active ? "text-emerald-700" : "text-slate-400"}>
                        {a.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {canMutate && (
                        <>
                          <button
                            onClick={() => toggleActive(a)}
                            className="text-[#0a2540] hover:underline mr-3 text-[11px]"
                            data-testid={`btn-toggle-${a.id}`}
                          >
                            {a.active ? "Disable" : "Enable"}
                          </button>
                          <button
                            onClick={() => remove(a)}
                            className="text-red-600 hover:text-red-700"
                            aria-label="Delete"
                            data-testid={`btn-del-ann-${a.id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
